import CryptoJS from "crypto-js";
import { useState, useEffect } from "react";
import { getWhatId, getSiteVariantType } from "./utils";

const { VITE_KEY, VITE_WHAT_ID } = import.meta.env;

export const useFrontViewedDepth = (type, offer_list_id = "") => {
    useEffect(() => {
        const modalExcludeTypes = ["ava-wheel", "fortune", "fortuneNew", "koleso", "ai", "face", "cpc"];
        if (modalExcludeTypes.includes(type)) return;
        const listBlock = document.getElementById("list__block");
        if (!listBlock) return;

        let position = 0;
        let dirty = true;
        let initialSendPending = true;
        const elementPositions = new Map();
        const observedElements = new Set();

        const sendViewedDepth = () => {
            if (!dirty) return;
            console.log("Максимальная просмотренная позиция:", position);

            if (window.risMetrika && typeof window.risMetrika.reachgoal_ch === "function") {
                window.risMetrika.reachgoal_ch({
                    event: "front_viewed_depth",
                    siteId: getWhatId() || VITE_WHAT_ID,
                    position,
                    additional_property: { offer_list_id },
                });
                dirty = false;
            }
        };

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    const childPosition = elementPositions.get(entry.target);
                    if (childPosition !== undefined && childPosition > position) {
                        position = childPosition;
                        dirty = true;
                    }
                });

                if (initialSendPending && position > 0) {
                    initialSendPending = false;
                    sendViewedDepth();
                }
            },
            { threshold: 0.5 },
        );

        const updateObservedElements = () => {
            const children = Array.from(listBlock.children);
            const currentElements = new Set(children);

            observedElements.forEach((element) => {
                if (currentElements.has(element)) return;

                observer.unobserve(element);
                observedElements.delete(element);
            });

            elementPositions.clear();
            children.forEach((child, index) => {
                if (["list-sentinel"].includes(child?.id)) return;
                elementPositions.set(child, index + 1);
                if (observedElements.has(child)) return;

                observedElements.add(child);
                observer.observe(child);
            });
        };

        const listObserver = new MutationObserver(updateObservedElements);

        updateObservedElements();
        listObserver.observe(listBlock, { childList: true });

        const handleVisibilityChange = () => {
            if (document.visibilityState !== "hidden") return;
            sendViewedDepth();
        };

        const intervalId = setInterval(sendViewedDepth, 20000);

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("pagehide", sendViewedDepth);
        console.log("=>>> Start listen front_viewed_depth");

        return () => {
            clearInterval(intervalId);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("pagehide", sendViewedDepth);
            listObserver.disconnect();
            observer.disconnect();
        };
    }, [type, offer_list_id]);
};

export const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value]);

    return debouncedValue;
};

export const debounceFunc = (func, delay) => {
    let timerId;
    return (...args) => {
        clearTimeout(timerId);
        timerId = setTimeout(() => func(...args), delay);
    };
};

export const useClickOutside = (val, ref, cb, extraExcludes = []) => {
    const handleClick = (e) => {
        //чтоб при клике на тост, куки не вырубалась модалка
        const excludes = [".p-toast", ".cookies", ...extraExcludes];
        for (let i = 0; i < excludes.length; i++) {
            const cl = excludes[i];
            const el = document.querySelector(cl);

            if (el && el.contains(e.target)) return;
        }
        //чтоб при клике на тост, куки не вырубалась модалка

        if (ref.current && !ref.current.contains(e.target)) {
            cb();
        }
    };

    useEffect(() => {
        if (val) {
            document.addEventListener("click", handleClick, { capture: true });
        } else {
            document.removeEventListener("click", handleClick, { capture: true });
        }

        return () => {
            document.removeEventListener("click", handleClick, { capture: true });
        };
    });
};

export const useGetHeightToScrollContainer = (id, cb) => {
    useEffect(() => {
        resizeFunc();
        window.addEventListener("resize", resizeFunc);

        return () => window.removeEventListener("resize", resizeFunc);
    }, []);

    const resizeFunc = () => {
        const mainContainer = document.getElementById("offset-container");
        const elem = document.getElementById(id);
        if (elem && mainContainer) cb(mainContainer.clientHeight + mainContainer.offsetTop - elem.getBoundingClientRect().top);
    };
};

export const useHandleResize = () => {
    const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth <= 820 : false));

    useEffect(() => {
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleResize = () => {
        setIsMobile(window.innerWidth <= 820);
    };

    return isMobile;
};

export const useElementCssHeight = (ref, cssVariableName, watchValue = null) => {
    useEffect(() => {
        const updateHeight = () => {
            if (!ref.current) return;

            document.documentElement.style.setProperty(cssVariableName, `${ref.current.offsetHeight}px`);
        };

        updateHeight();

        if (!ref.current) return undefined;

        if (!window.ResizeObserver) {
            window.addEventListener("resize", updateHeight);
            return () => window.removeEventListener("resize", updateHeight);
        }

        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(ref.current);

        return () => resizeObserver.disconnect();
    }, [cssVariableName, ref, watchValue]);
};

export const useInViewport = (elems = [], cb, deps, offerLength) => {
    useEffect(() => {
        if (deps) return;

        if (!offerLength) return;

        const debouncedScrollHandler = debounceFunc(scrollHandler, 2000);

        setTimeout(() => {
            debouncedScrollHandler();
            document.addEventListener("scroll", debouncedScrollHandler);

            return () => {
                document.removeEventListener("scroll", debouncedScrollHandler);
            };
        }, 40);
    }, [deps, offerLength]);

    let tmpArr = [];

    const scrollHandler = (e) => {
        const screenHeight = document.documentElement.clientHeight;
        const nodeArr = elems
            .reduce((acc, row) => {
                const node = document.querySelectorAll(row);
                acc.push([...node]);
                return acc;
            }, [])
            .flat();

        nodeArr.forEach((node) => {
            const rect = node.getBoundingClientRect();
            if (rect.top >= 0 && rect.bottom <= screenHeight) {
                if (!tmpArr.some((s) => s.text == node.id)) {
                    cb(node);
                    tmpArr.push({ node, text: node.id });
                }
            } else {
                tmpArr = tmpArr.filter((f) => f.text !== node.id);
            }
        });
    };
};

export const useCrypto = () => {
    const secretKey = VITE_KEY;
    const encryptData = (data) => {
        const jsonString = JSON.stringify(data);
        return CryptoJS.AES.encrypt(jsonString, secretKey).toString();
    };

    const decryptData = (encryptedData) => {
        try {
            const decryptedBytes = CryptoJS.AES.decrypt(encryptedData, secretKey);
            const decryptedJsonString = decryptedBytes.toString(CryptoJS.enc.Utf8);
            return JSON.parse(decryptedJsonString);
        } catch (error) {
            console.error("Ошибка при расшифровке:", error.message);
            throw error;
        }
    };

    return { encryptData, decryptData };
};

export const loadLazyComponent = (component) => ({ type: "lazy", component });

export const loadUsualComponent = (component) => ({ type: "component", component });

export const useLoadComponents = (components = {}, keys = []) => {
    const [loaded, setLoaded] = useState({});

    useEffect(() => {
        let isActive = true;
        const retries = 1;

        keys.forEach(async (key) => {
            const entry = components[key];

            if (!entry || !["lazy", "component"].includes(entry?.type)) {
                setLoaded((prev) => ({ ...prev, [key]: null }));
                return;
            }

            for (let attempt = 0; attempt <= retries; attempt++) {
                try {
                    const mod = entry.type === "lazy" ? await entry.component() : entry?.component;

                    if (isActive) {
                        setLoaded((prev) => ({ ...prev, [key]: mod?.default || mod || null }));
                    }
                    return;
                } catch (error) {
                    if (attempt === retries && isActive) {
                        console.error(`[useLoadComponents] Failed to load component "${key}"`, error);
                        // Помечаем ключ как завершённый (с ошибкой), чтобы не зависнуть на Loader навсегда.
                        setLoaded((prev) => ({ ...prev, [key]: null }));
                    }
                }
            }
        });

        return () => (isActive = false);
    }, [components, keys]);

    return loaded;
};
const EmptyComponent = () => null;

// продуктовая и майн версии сайта всегда получают тест 2613 вместо дефолта, если бэкенд не назначил другой вариант
const FORCED_SITE_VARIANTS = ["main", "product"];
const FORCED_AB_PREFIX = "dev_2613__";

export const useAbTestComponent = ({ init, abTests, defaultComponent, LoadingComp = EmptyComponent }) => {
    const [loaded, setLoaded] = useState(null);
    let matchKey = null;

    for (const key in abTests) {
        const [code, ver] = key.split("__");
        if (init?.data?._ab_m?.[code]?._d?.colorHeader === ver) {
            matchKey = key;
            break;
        }
    }

    // если бэкенд назначил любой тест, не форсим 2613 — иначе компоненты без варианта под этот тест смешивают дизайны
    const hasBackendAb = Object.values(init?.data?._ab_m || {}).some((test) => test?._d?.colorHeader);

    if (!matchKey && !hasBackendAb) {
        const siteVariant = getSiteVariantType();
        // нет куки (сайт открыт напрямую, без родителя) приравнивается к продуктовой/майн версии
        if (siteVariant === null || FORCED_SITE_VARIANTS.includes(siteVariant)) {
            matchKey = Object.keys(abTests).find((key) => key.startsWith(FORCED_AB_PREFIX)) || null;
        }
    }

    const entry = matchKey ? abTests[matchKey] : null;

    useEffect(() => {
        let isActive = true;
        const retries = 1;

        if (!entry || entry.type !== "lazy") return () => (isActive = false); // если не lazy тот и не пускаем дальше, потому что в loadComponent логика именно для загрузки lazy

        setLoaded(null);

        const loadComponent = async () => {
            for (let attempt = 0; attempt <= retries; attempt++) {
                try {
                    const mod = await entry.component();
                    if (isActive) setLoaded(mod?.default || mod || null);

                    return;
                } catch (error) {
                    if (attempt === retries && isActive) {
                        console.error(`[useAbTestComponent] Failed to load component "${matchKey}"`, error);
                        setLoaded(defaultComponent || EmptyComponent);
                    }
                }
            }
        };

        loadComponent();

        return () => (isActive = false);
    }, [entry, matchKey, defaultComponent]);

    // выбрасываем сразу компоненты без сетстейтов
    if (!matchKey || !entry || !["lazy", "component"].includes(entry?.type)) return defaultComponent || EmptyComponent;
    if (entry.type === "component") return entry.component || defaultComponent || EmptyComponent;
    // выбрасываем сразу компоненты без сетстейтов

    return loaded || LoadingComp;
};
